import { Link } from 'react-router-dom';
import { PiArrowRightBold, PiWarningBold, PiInfoBold, PiCheckCircleBold } from 'react-icons/pi';
import { cn } from '@v2/utils/cn';
import type { CommissionExecutiveByRole, CommissionExecutiveByStatus, CommissionExecutiveDashboard, CommissionExecutiveTopEarner } from './types';

const brl = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(v ?? 0);

const brlCompact = (v: number) => {
    if (v >= 1_000_000) return `R$ ${(v / 1_000_000).toFixed(1)}M`;
    if (v >= 1_000) return `R$ ${(v / 1_000).toFixed(0)}k`;
    return brl(v);
};

export function fmtMonthShort(ym: string) {
    const [y, m] = ym.split('-').map(Number);
    return new Date(y, m - 1, 1).toLocaleDateString('pt-BR', { month: 'short' }).replace('.', '');
}

type ChartPad = { t: number; r: number; b: number; l: number };

function scaleY(value: number, max: number, height: number) {
    if (max <= 0) return height;
    return height - (value / max) * height;
}

/** Barras agrupadas + linha de acumulado pago */
export function MonthlyComboChart({
    labels,
    paid,
    accrued,
    cumulative,
    dealVolume,
}: {
    labels: string[];
    paid: number[];
    accrued: number[];
    cumulative: number[];
    dealVolume?: number[];
}) {
    const w = 920;
    const h = 320;
    // Margens: direita maior para acomodar 2 eixos Y à direita
    const pad: ChartPad = { t: 24, r: 100, b: 44, l: 72 };
    const chartW = w - pad.l - pad.r;
    const chartH = h - pad.t - pad.b;
    const n = labels.length || 1;
    const groupW = chartW / n;

    const hasDealVol = dealVolume && dealVolume.some(v => v > 0);

    // Eixo Y esquerdo: comissões (pago + gerado + acumulado)
    const maxComm = Math.max(...paid, ...accrued, ...cumulative, 1);
    // Eixo Y direito: volume de deals (escala separada)
    const maxDeal = Math.max(...(dealVolume ?? []), 1);

    const barW = Math.min(14, groupW / 3.5);

    // Helpers de escala
    const yComm = (v: number) => pad.t + (1 - v / maxComm) * chartH;
    const yDeal = (v: number) => pad.t + (1 - v / maxDeal) * chartH;

    const linePoints = cumulative.map((v, i) => {
        const x = pad.l + groupW * i + groupW / 2;
        return `${x},${yComm(v)}`;
    }).join(' ');

    const yTicks = 5;

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${w} ${h}`} className="min-w-[640px] w-full" role="img" aria-label="Evolução mensal de comissões">
                {/* Grid horizontal (baseado na escala de comissões) */}
                {Array.from({ length: yTicks + 1 }, (_, i) => (maxComm / yTicks) * i).map((tick) => {
                    const y = yComm(tick);
                    return (
                        <g key={tick}>
                            <line x1={pad.l} y1={y} x2={w - pad.r} y2={y} className="stroke-gray-100 dark:stroke-zinc-800" strokeWidth={1} />
                            {/* Eixo Y esquerdo — comissões */}
                            <text x={pad.l - 8} y={y + 4} textAnchor="end" className="fill-emerald-600 dark:fill-emerald-500 text-[10px]">
                                {brlCompact(tick)}
                            </text>
                        </g>
                    );
                })}

                {/* Eixo Y direito — volume de deals */}
                {hasDealVol && Array.from({ length: yTicks + 1 }, (_, i) => (maxDeal / yTicks) * i).map((tick) => {
                    const y = yDeal(tick);
                    return (
                        <text key={`r${tick}`} x={w - pad.r + 8} y={y + 4} textAnchor="start" className="fill-blue-500 dark:fill-blue-400 text-[10px]">
                            {brlCompact(tick)}
                        </text>
                    );
                })}

                {/* Barras */}
                {labels.map((ym, i) => {
                    const gx = pad.l + groupW * i + groupW / 2;
                    const paidH = Math.max(0, chartH - (yComm(paid[i]) - pad.t));
                    const accruedH = Math.max(0, chartH - (yComm(accrued[i]) - pad.t));
                    const dealVolVal = dealVolume?.[i] ?? 0;
                    const dealH = hasDealVol ? Math.max(0, chartH - (yDeal(dealVolVal) - pad.t)) : 0;

                    // 2 barras (pago+gerado) à esquerda do centro; barra de volume à direita
                    const commStartX = gx - barW - 2;
                    const dealStartX = gx + 2;

                    return (
                        <g key={ym}>
                            {/* Pago (escala esquerda) */}
                            <rect x={commStartX} y={pad.t + chartH - paidH} width={barW} height={paidH} rx={3} className="fill-emerald-500">
                                <title>{`${fmtMonthShort(ym)} — Pago: ${brl(paid[i])}`}</title>
                            </rect>
                            {/* Gerado (escala esquerda) */}
                            <rect x={commStartX + barW / 2 + 1} y={pad.t + chartH - accruedH} width={barW} height={accruedH} rx={3} className="fill-orange-400">
                                <title>{`${fmtMonthShort(ym)} — Gerado: ${brl(accrued[i])}`}</title>
                            </rect>
                            {/* Volume de deals (escala direita) */}
                            {hasDealVol && (
                                <rect x={dealStartX} y={pad.t + chartH - dealH} width={barW} height={dealH} rx={3} className="fill-blue-400/50">
                                    <title>{`${fmtMonthShort(ym)} — Volume vendas: ${brl(dealVolVal)}`}</title>
                                </rect>
                            )}
                            <text x={gx} y={h - 14} textAnchor="middle" className="fill-gray-500 text-[10px] capitalize">
                                {fmtMonthShort(ym)}
                            </text>
                        </g>
                    );
                })}

                {/* Linha acumulado pago (escala esquerda) */}
                <polyline
                    points={linePoints}
                    fill="none"
                    className="stroke-emerald-700 dark:stroke-emerald-400"
                    strokeWidth={2}
                    strokeDasharray="5 3"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                />
                {cumulative.map((v, i) => {
                    const x = pad.l + groupW * i + groupW / 2;
                    const y = yComm(v);
                    return (
                        <circle key={i} cx={x} cy={y} r={3} className="fill-emerald-600 dark:fill-emerald-400 stroke-white" strokeWidth={1.5}>
                            <title>{`Acumulado: ${brl(v)}`}</title>
                        </circle>
                    );
                })}

                {/* Labels dos eixos */}
                <text x={pad.l - 8} y={pad.t - 8} textAnchor="end" className="fill-emerald-600 dark:fill-emerald-500 text-[9px] font-semibold">
                    Comissão
                </text>
                {hasDealVol && (
                    <text x={w - pad.r + 8} y={pad.t - 8} textAnchor="start" className="fill-blue-500 dark:fill-blue-400 text-[9px] font-semibold">
                        Vol. Vendas
                    </text>
                )}
            </svg>
            <div className="mt-2 flex flex-wrap justify-center gap-4 text-[11px] text-gray-500 dark:text-zinc-400">
                <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm bg-emerald-500" /> Pago</span>
                <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm bg-orange-400" /> Gerado</span>
                <span className="flex items-center gap-1.5"><span className="size-2.5 rounded-full border-2 border-emerald-600 dark:border-emerald-400 bg-white dark:bg-zinc-900" /> Acumulado pago</span>
                {hasDealVol && <span className="flex items-center gap-1.5"><span className="size-3 rounded-sm bg-blue-400/70" /> Volume vendas (eixo dir.)</span>}
            </div>
        </div>
    );
}

const STATUS_COLORS: Record<string, string> = {
    paid: '#10b981',
    nf_submitted: '#3b82f6',
    awaiting_nf: '#f59e0b',
    pending: '#9ca3af',
    cancelled: '#ef4444',
};

/** Donut — distribuição por status (valor) */
export function StatusDonutChart({ items }: { items: CommissionExecutiveByStatus[] }) {
    const open = items.filter((s) => s.status !== 'cancelled' && s.amount > 0);
    const total = open.reduce((s, i) => s + i.amount, 0);
    const size = 160;
    const r = 58;
    const cx = size / 2;
    const cy = size / 2;
    const circ = 2 * Math.PI * r;

    if (total <= 0) {
        return <p className="py-8 text-center text-sm text-gray-400">Sem valores no funil.</p>;
    }

    let offset = 0;
    const segments = open.map((item) => {
        const pct = item.amount / total;
        const dash = pct * circ;
        const seg = (
            <circle
                key={item.status}
                cx={cx}
                cy={cy}
                r={r}
                fill="none"
                stroke={STATUS_COLORS[item.status] ?? '#d1d5db'}
                strokeWidth={22}
                strokeDasharray={`${dash} ${circ - dash}`}
                strokeDashoffset={-offset}
                transform={`rotate(-90 ${cx} ${cy})`}
            >
                <title>{`${item.status_label}: ${brl(item.amount)} (${item.count} reg.)`}</title>
            </circle>
        );
        offset += dash;
        return seg;
    });

    return (
        <div className="flex flex-col items-center gap-4 sm:flex-row sm:items-start">
            <div className="relative shrink-0">
                <svg width={size} height={size}>
                    <circle cx={cx} cy={cy} r={r} fill="none" className="stroke-gray-100 dark:stroke-zinc-800" strokeWidth={22} />
                    {segments}
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                    <span className="text-[10px] uppercase text-gray-400">Total</span>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">{brlCompact(total)}</span>
                </div>
            </div>
            <ul className="min-w-0 flex-1 space-y-2">
                {open.map((item) => (
                    <li key={item.status} className="flex items-center justify-between gap-2 text-xs">
                        <span className="flex items-center gap-2 truncate">
                            <span className="size-2.5 shrink-0 rounded-full" style={{ background: STATUS_COLORS[item.status] }} />
                            <span className="font-medium text-gray-700 dark:text-zinc-200">{item.status_label}</span>
                        </span>
                        <span className="shrink-0 tabular-nums text-gray-500">
                            {Math.round((item.amount / total) * 100)}% · {brl(item.amount)}
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

/** Barras horizontais empilhadas — pago vs pipeline por nível */
export function RoleStackedChart({ items }: { items: CommissionExecutiveByRole[] }) {
    const max = Math.max(...items.map((r) => r.paid + r.pipeline), 1);

    return (
        <ul className="space-y-4">
            {items.map((r) => {
                const paidPct = (r.paid / max) * 100;
                const pipePct = (r.pipeline / max) * 100;
                return (
                    <li key={r.commission_role}>
                        <div className="mb-1 flex items-center justify-between text-xs">
                            <span className="font-semibold text-gray-800 dark:text-zinc-200">{r.role_label}</span>
                            <span className="text-gray-500">{r.count} reg.</span>
                        </div>
                        <div className="flex h-7 overflow-hidden rounded-lg bg-gray-100 dark:bg-zinc-800">
                            {r.paid > 0 && (
                                <div
                                    className="flex items-center justify-end bg-emerald-500 px-1.5 text-[10px] font-bold text-white"
                                    style={{ width: `${Math.max(paidPct, r.paid > 0 ? 8 : 0)}%` }}
                                    title={`Pago: ${brl(r.paid)}`}
                                >
                                    {paidPct > 15 && brlCompact(r.paid)}
                                </div>
                            )}
                            {r.pipeline > 0 && (
                                <div
                                    className="flex items-center justify-end bg-amber-400 px-1.5 text-[10px] font-bold text-amber-950"
                                    style={{ width: `${Math.max(pipePct, r.pipeline > 0 ? 8 : 0)}%` }}
                                    title={`Pipeline: ${brl(r.pipeline)}`}
                                >
                                    {pipePct > 15 && brlCompact(r.pipeline)}
                                </div>
                            )}
                        </div>
                    </li>
                );
            })}
            <div className="flex gap-4 text-[10px] text-gray-500">
                <span className="flex items-center gap-1"><span className="size-2.5 rounded bg-emerald-500" /> Pago</span>
                <span className="flex items-center gap-1"><span className="size-2.5 rounded bg-amber-400" /> A pagar</span>
            </div>
        </ul>
    );
}

/** Barras verticais — top comissionados */
export function TopEarnersChart({ items }: { items: CommissionExecutiveTopEarner[] }) {
    const top = items.slice(0, 6);
    const max = Math.max(...top.map((e) => e.paid + e.pipeline), 1);
    const w = 320;
    const h = 200;
    const pad = { t: 12, r: 8, b: 48, l: 8 };
    const chartW = w - pad.l - pad.r;
    const chartH = h - pad.t - pad.b;
    const barSlot = chartW / top.length;

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${w} ${h}`} className="mx-auto w-full max-w-sm">
                {top.map((e, i) => {
                    const paidH = (e.paid / max) * chartH;
                    const pipeH = (e.pipeline / max) * chartH;
                    const x = pad.l + barSlot * i + barSlot * 0.2;
                    const bw = barSlot * 0.6;
                    const firstName = e.name.split(/\s+/)[0] ?? e.name;
                    return (
                        <g key={e.user_id}>
                            <rect
                                x={x}
                                y={pad.t + chartH - paidH - pipeH}
                                width={bw}
                                height={paidH}
                                rx={2}
                                className="fill-emerald-500"
                            >
                                <title>{`${e.name} — Pago: ${brl(e.paid)}`}</title>
                            </rect>
                            {e.pipeline > 0 && (
                                <rect
                                    x={x}
                                    y={pad.t + chartH - pipeH}
                                    width={bw}
                                    height={pipeH}
                                    rx={2}
                                    className="fill-amber-300"
                                >
                                    <title>{`${e.name} — Pipeline: ${brl(e.pipeline)}`}</title>
                                </rect>
                            )}
                            <text
                                x={x + bw / 2}
                                y={h - 8}
                                textAnchor="middle"
                                className="fill-gray-600 text-[9px]"
                            >
                                {firstName.length > 8 ? `${firstName.slice(0, 7)}…` : firstName}
                            </text>
                        </g>
                    );
                })}
            </svg>
            <ul className="mt-2 space-y-1">
                {top.map((e) => (
                    <li key={e.user_id}>
                        <Link
                            to={`/commissioning/sellers/${e.user_id}`}
                            className="flex items-center justify-between rounded-md px-2 py-1 text-xs hover:bg-orange-50 dark:hover:bg-orange-950/20"
                        >
                            <span className="truncate font-medium">{e.name}</span>
                            <span className="shrink-0 font-bold text-emerald-700">{brl(e.paid)}</span>
                        </Link>
                    </li>
                ))}
            </ul>
        </div>
    );
}

export type DecisionAlert = {
    severity: 'critical' | 'warning' | 'info' | 'success';
    title: string;
    message: string;
    action_to?: string;
    action_label?: string;
};

export function buildDecisionAlerts(dash: CommissionExecutiveDashboard): DecisionAlert[] {
    const { kpis, by_status } = dash;
    const alerts: DecisionAlert[] = [];

    if (kpis.awaiting_nf_count > 0) {
        alerts.push({
            severity: 'warning',
            title: `${kpis.awaiting_nf_count} comissão(ões) aguardando NF`,
            message: `${brl(kpis.awaiting_nf_amount)} bloqueados até o vendedor enviar nota fiscal.`,
            action_to: 'records?status=awaiting_nf',
            action_label: 'Cobrar NF',
        });
    }

    const nfSubmitted = by_status.find((s) => s.status === 'nf_submitted');
    if (nfSubmitted && nfSubmitted.count > 0) {
        alerts.push({
            severity: 'info',
            title: `${nfSubmitted.count} NF(s) pronta(s) para pagamento`,
            message: `${brl(nfSubmitted.amount)} aguardando liberação financeira.`,
            action_to: 'payment-approvals',
            action_label: 'Aprovar pagamentos (RH)',
        });
    }

    const gap = kpis.accrued_mtd - kpis.paid_mtd;
    if (gap > 1000 && kpis.accrued_mtd > 0) {
        alerts.push({
            severity: 'info',
            title: 'Gap gerado × pago no mês',
            message: `${brl(gap)} gerados este mês ainda não foram pagos (${Math.round((gap / kpis.accrued_mtd) * 100)}% do gerado).`,
        });
    }

    if (kpis.mom_paid_pct != null && kpis.mom_paid_pct <= -15) {
        alerts.push({
            severity: 'critical',
            title: 'Queda acentuada no pagamento',
            message: `Pago no mês ${kpis.mom_paid_pct}% abaixo do mês anterior (${brl(kpis.paid_prev_month)} → ${brl(kpis.paid_mtd)}).`,
        });
    } else if (kpis.mom_paid_pct != null && kpis.mom_paid_pct >= 20) {
        alerts.push({
            severity: 'success',
            title: 'Crescimento forte no pagamento',
            message: `+${kpis.mom_paid_pct}% vs mês anterior — ${brl(kpis.paid_mtd)} pagos.`,
        });
    }

    if (kpis.pipeline_amount > kpis.paid_ytd * 0.5 && kpis.pipeline_amount > 50000) {
        alerts.push({
            severity: 'warning',
            title: 'Pipeline elevado',
            message: `${brl(kpis.pipeline_amount)} em comissões ainda não pagas — revisar fluxo de NF e financeiro.`,
            action_to: 'records',
            action_label: 'Ver pipeline',
        });
    }

    return alerts.slice(0, 4);
}

export function DecisionAlertsPanel({ alerts }: { alerts: DecisionAlert[] }) {
    if (alerts.length === 0) return null;

    const styles = {
        critical: 'border-rose-200 bg-rose-50 text-rose-900 dark:border-rose-900 dark:bg-rose-950/40 dark:text-rose-200',
        warning: 'border-amber-200 bg-amber-50 text-amber-950 dark:border-amber-700/50 dark:bg-amber-500/10 dark:text-amber-200',
        info: 'border-blue-200 bg-blue-50 text-blue-950 dark:border-blue-700/50 dark:bg-blue-500/10 dark:text-blue-200',
        success: 'border-emerald-200 bg-emerald-50 text-emerald-950 dark:border-emerald-700/50 dark:bg-emerald-500/10 dark:text-emerald-200',
    };

    const icons = {
        critical: PiWarningBold,
        warning: PiWarningBold,
        info: PiInfoBold,
        success: PiCheckCircleBold,
    };

    return (
        <section className="rounded-2xl border border-orange-200 bg-gradient-to-r from-orange-50/80 to-white p-4 dark:border-orange-900/50 dark:from-orange-950/20 dark:to-zinc-900">
            <h2 className="mb-3 text-xs font-bold uppercase tracking-wider text-orange-800 dark:text-orange-300">
                Ações prioritárias
            </h2>
            <div className="grid gap-3 sm:grid-cols-2">
                {alerts.map((a, i) => {
                    const Icon = icons[a.severity];
                    return (
                        <div
                            key={i}
                            className={cn('flex gap-3 rounded-xl border p-4', styles[a.severity])}
                        >
                            <Icon className="mt-0.5 size-5 shrink-0 opacity-80" />
                            <div className="min-w-0 flex-1">
                                <p className="text-sm font-bold">{a.title}</p>
                                <p className="mt-0.5 text-xs opacity-90">{a.message}</p>
                                {a.action_to && a.action_label && (
                                    <Link
                                        to={a.action_to}
                                        className="mt-2 inline-flex items-center gap-1 text-xs font-semibold underline underline-offset-2"
                                    >
                                        {a.action_label}
                                        <PiArrowRightBold className="size-3" />
                                    </Link>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>
        </section>
    );
}

/** Sparkline para KPI cards */
export function Sparkline({ values, color = '#10b981' }: { values: number[]; color?: string }) {
    if (values.length < 2) return null;
    const w = 80;
    const h = 28;
    const max = Math.max(...values, 1);
    const min = Math.min(...values);
    const range = max - min || 1;
    const points = values.map((v, i) => {
        const x = (i / (values.length - 1)) * w;
        const y = h - ((v - min) / range) * (h - 4) - 2;
        return `${x},${y}`;
    }).join(' ');

    return (
        <svg width={w} height={h} className="opacity-80">
            <polyline points={points} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}
